import React, {useState, useEffect, useContext, useRef} from 'react';
import useOnClickOutside from "../hooks/useOnClickOutside";
import useCurrentShortcut from "../hooks/useCurrentShortcut";
import Notifications from './Notifications';
import NavigationDrawer from './NavigationDrawer';
import SeriesSelect from './SeriesSelect';
import Search from './Search';
import PromptSearch from './PromptSearch';
import SeriesNavigation from './SeriesNavigation';
import {AdminSettingsIcon, BookmarksIcon, SpeechHelpIcon, HelpIcon, SearchIcon, SettingsIcon, CogIcon, LogoutIcon, NotificationsIcon, ReportsIcon, GoIcon, PinIcon} from '../common/icons';
import {UserContext} from '../common/UserContext';
import {BrandContext} from "../common/BrandContext";
import Bookmarks from "./Bookmarks";
import Brands from "./Brands";
import {ContentContext} from "../common/ContentContext";
import {CompressButton, MenuCompressButton} from "./CompressButton";
import RightMenuSwitch from "./RightMenuSwitch";
import {TemplateContext} from "../common/TemplateContext";
import MenuButton from "./MenuButton";
import ChildMenu from "./ChildMenu";
import CircularLoader from "../common/CircularLoader";
import {DashboardIcon} from "../common/icons";
import UserReview from "../review/UserReview";
import AdminSchoolSelector from "./AdminSchoolSelector";
import NavAccessibilityWrap from "./NavAccessibilityWrap";
import CMSSearch from "../cms/CMSSearch";
import StudentDiagnosticMenuWidget from "../diagnosticReporting/StudentDiagnosticMenuWidget"
import EditLoader from "../common/EditLoader";
import SaveLoader from "../common/SaveLoader";
import {useScreenMode} from "../common/context/ScreenModeContext";

const Navigation = props => {
    const {visible, updateVisible, globalShortcuts, children} = props;
    const {user, updateLeftMenuClosed, menuPosition, userLoading} = useContext(UserContext);
    const {contentLoading, contentSaving, contentEditing} = useContext(ContentContext);
    const {isSeriesNavVisible, setSeriesNavVisible} = useContext(TemplateContext);
    const brandConfig = useContext(BrandContext);
    const {loadShortcut} = useCurrentShortcut();
    const [lowerGlobalShortcuts, setLowerGlobalShortcuts] = useState([]);
    const [isDrawerVisible, setDrawerVisible] = useState(false);
    const [drawerComponentName, setDrawerComponentName] = useState(undefined);
    const [isMenuVisible, setMenuVisible] = useState(false);
    const [selectedShortcut, setSelectedShortcut] = useState();
    const [openMenuButton, setOpenMenuButton] = useState();
    const navigationRef = useRef();
    const mainMenuRef = useRef();
    const {smallScreenMode} = useScreenMode();

    useEffect(() => {
        if(window.location.href.includes('showNavigation=true')) {
            setSeriesNavVisible(true);
        }
    }, []);

    useEffect(() => {
        if (smallScreenMode) {
            if ((isDrawerVisible || isMenuVisible || isSeriesNavVisible)) {
                updateVisible(true);
            } else if (visible) {
                updateVisible(false);
            }
        } else {
            updateVisible(true);
        }
    }, [smallScreenMode, isSeriesNavVisible]);

    useEffect(() => {
        const nav = document.getElementById('nav');
        const navR = document.getElementById('nav-r');

        const resizeCanvas = () => {
            if (globalReactFunctions !== undefined) {
                if (globalReactFunctions.resizeScorcherCanvas !== undefined) {
                    globalReactFunctions.resizeScorcherCanvas();
                }
                if (globalReactFunctions.resizeWidgetCanvas !== undefined) {
                    globalReactFunctions.resizeWidgetCanvas();
                }
            }
        }

        const navTransitionEnd = () => {
            //resizes scorcher and widget canvas elements
            resizeCanvas();

            //dispatchesEvent global event at end of transition
            window.dispatchEvent(new Event('menuSizeChanged'));
        }

        resizeCanvas();
        nav.addEventListener('transitionend', navTransitionEnd,false);
        navR.addEventListener('transitionend', navTransitionEnd,false);
        return () => {
            nav.removeEventListener('transitionend', navTransitionEnd);
            navR.removeEventListener('transitionend', navTransitionEnd);
        }
    }, [user]);

    useEffect(() => {
        if (globalShortcuts) {
            let lowerGlobalShortcuts = globalShortcuts.filter(globalShortcut => globalShortcut.shortcutLocation === 'BOTTOM_MENU');
            setLowerGlobalShortcuts(lowerGlobalShortcuts);
        } else {
            setLowerGlobalShortcuts([]);
        }
    }, [globalShortcuts]);

    if (globalReactFunctions !== undefined) {
        globalReactFunctions.setDrawerVisible = setDrawerVisible;
    }

    const menuComponents = {
        RightMenuSwitch: RightMenuSwitch,
        Bookmarks: Bookmarks,
        Notifications: Notifications,
        Search: Search,
        PromptSearch: PromptSearch,
        Brands: Brands,
        ChildMenu: ChildMenu,
        UserReview: UserReview,
        AdminSchoolSelector: AdminSchoolSelector,
        CMSSearch: CMSSearch,
        StudentDiagnosticMenuWidget: StudentDiagnosticMenuWidget,
    };

    const iconComponents = {
        DashboardIcon: DashboardIcon,
        AdminSettingsIcon: AdminSettingsIcon,
        SearchIcon: SearchIcon,
        BookmarksIcon: BookmarksIcon,
        HelpIcon: HelpIcon,
        SpeechHelpIcon: SpeechHelpIcon,
        CogIcon: CogIcon,
        SettingsIcon: SettingsIcon,
        LogoutIcon: LogoutIcon,
        NotificationsIcon: NotificationsIcon,
        ReportsIcon: ReportsIcon,
        GoIcon: GoIcon,
        Default: HelpIcon,
        PinIcon: PinIcon,
    };

    useOnClickOutside(navigationRef, visible, () => {
        if (smallScreenMode) {
            updateVisible(false);
            setDrawerVisible(false);
        }
    });

    const handleMenuClick = shortcut => {
        setMenuVisible(false);
        setSelectedShortcut(undefined);
        if (shortcut && openMenuButton !== shortcut.shortcutId) {
            setOpenMenuButton(shortcut.shortcutId);
            loadShortcut(shortcut);
        } else {
            setOpenMenuButton(false);
        }
    }

    const handleDrawerClick = (shortcut, menuComponent, menuComponentName) => {
        if (menuComponent === undefined) {
            return;
        }

        if(drawerComponentName === menuComponentName && isDrawerVisible){
            setDrawerVisible(false);
        }else{
            setDrawerVisible(true);
        }
        setDrawerComponentName(menuComponentName);
        setSelectedShortcut(shortcut);
    }

    // const toggleDrawerVisibility = ()=>{
    //     setDrawerVisible(!isDrawerVisible);
    // }

    const selectedComponentName = (selectedShortcut !== undefined && selectedShortcut.menuComponent) ? selectedShortcut.menuComponent : undefined;
    const selectedComponent = selectedComponentName !== undefined ? menuComponents[selectedComponentName] :undefined;

    if(user.publicSession && user.guideMode) {
        return null;
    }

    return (
        <div ref={navigationRef} className={'ch-nav' + (smallScreenMode ? ' ch-nav-ssm' : '') + (!visible && !isDrawerVisible ? ' ch-nav-hide' : '')}>
            <div className={'ch-nav-columns'}>
                <NavAccessibilityWrap ref={mainMenuRef} aria-label={"Global Navigation"} className={'ch-nav-global ch-nav-column ch-nav-rail-column flexColumn flexSpaceBetween'}>
                    <div role="menu" className="ch-nav-global-top flex flexColumn ch-nav-group">
                        {globalShortcuts.map((shortcut, index) => {
                            const iconComponentName = (shortcut.iconComponent ? shortcut.iconComponent : 'Default');
                            const iconComponent = iconComponents[iconComponentName];

                            const menuComponentName = (shortcut.menuComponent ? shortcut.menuComponent : undefined);
                            const menuComponent = menuComponents[menuComponentName];

                            if (shortcut.actForUser && user.actForUserId == undefined) {
                                return null;
                            } else if (shortcut.multiBrand && (user.brandCodes == undefined || user.brandCodes.length <= 1)) {
                                return null;
                            } else if (shortcut.adminShortcut && !brandConfig.enableAdminShortcuts && !user.adminUser && user.actForUserId == undefined) {
                                return null;
                            } else if (shortcut.shortcutId == 153 && user.authorityId != 2) {
                                return null;
                            } else if (shortcut.shortcutId == 177 && !user.testResultId) {
                                return null;
                            } else if (shortcut.shortcutLocation == "BOTTOM_MENU") {
                                return null;
                            } else {
                                return (
                                    <MenuButton
                                        key={shortcut.shortcutId}
                                        selected={selectedShortcut === shortcut}
                                        shortcut={shortcut}
                                        handleClick={handleMenuClick}
                                        handleDrawerClick={(shortcut) => handleDrawerClick(shortcut, menuComponent, menuComponentName)}
                                        handleClose={handleMenuClick}
                                        visible={openMenuButton === shortcut.shortcutId}
                                        iconComponent={iconComponent}
                                        menuComponent={menuComponent}
                                        iconComponents={iconComponents}
                                        menuComponents={menuComponents}
                                        tabkey={shortcut.shortcutId}
                                        tabIndex={index == 0 ? 0 : -1}
                                        menuWidget={shortcut.menuWidget}
                                        highlight={shortcut.highlight}
                                    />
                                )
                            }
                        })}
                        <EditLoader visible={!contentSaving && contentEditing}/>
                        <SaveLoader visible={contentSaving}/>
                        <CircularLoader visible={userLoading || contentLoading} pageCentered={false}/>
                    </div>
                    <div className="ch-nav-global-bottom flex flexColumn">
                        {lowerGlobalShortcuts.map((shortcut, index) => {
                            const iconComponentName = (shortcut.iconComponent ? shortcut.iconComponent : 'Default');
                            const iconComponent = iconComponents[iconComponentName];

                            const menuComponentName = (shortcut.menuComponent ? shortcut.menuComponent : undefined);
                            const menuComponent = menuComponents[menuComponentName];

                            let className = "";
                            if (shortcut.shortcutId == 165) {
                                className = "ch-icon-btn-lg"
                            }
                            if (shortcut.shortcutLocation != "BOTTOM_MENU") {
                                return null;
                            } else {
                                return (
                                    <MenuButton
                                        key={shortcut.shortcutId}
                                        className={className}
                                        selected={selectedShortcut === shortcut}
                                        shortcut={shortcut}
                                        handleClick={handleMenuClick}
                                        handleDrawerClick={(shortcut) => handleDrawerClick(shortcut, menuComponent, menuComponentName)}
                                        handleClose={() => setOpenMenuButton(null)}
                                        visible={openMenuButton === shortcut.shortcutId}
                                        iconComponent={iconComponent}
                                        menuComponent={menuComponent}
                                        iconComponents={iconComponents}
                                        menuComponents={menuComponents}
                                        tabkey={shortcut.shortcutId}
                                        menuWidget={shortcut.menuWidget}
                                        highlight={shortcut.highlight}
                                    />
                                )
                            }
                        })}
                    </div>
                </NavAccessibilityWrap>
                {!user.expired && (!user.publicSession || user.syllabusGroupId) &&
                    <>
                        <div className={'ch-nav-contextual ch-nav-column'}>
                            <SeriesSelect className={'ch-nav-contextual-head'} visible={isSeriesNavVisible} compressed={menuPosition.leftMenuClosed} smallScreenMode={smallScreenMode} onClick={() => {setSeriesNavVisible(!isSeriesNavVisible);}} />
                            <div className={'ch-nav-contextual-body'}>
                                {children}
                            </div>
                        </div>
                        <SeriesNavigation visible={isSeriesNavVisible} callback={() => {setSeriesNavVisible(false);}} smallScreenMode={smallScreenMode} outsideClickIgnoreClass={'ch-series-select'} />
                        <NavigationDrawer visible={isDrawerVisible} setVisible={setDrawerVisible} parentMenu={mainMenuRef.current} componentName={drawerComponentName} callback={() => {setDrawerVisible(false);}}></NavigationDrawer>
                        <MenuCompressButton right={menuPosition.leftMenuClosed} hidden={isSeriesNavVisible || smallScreenMode} onClick={() => {updateLeftMenuClosed(!menuPosition.leftMenuClosed);setSeriesNavVisible(false);}} />
                    </>
                }
            </div>
        </div>
    );
}

export default Navigation;